import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Button, Stack, FormControlLabel, Checkbox, CircularProgress } from '@mui/material';
import { useSignUpStore } from '../../../application/store/SignUpStore';
import { container } from '@infrastructure/container';


const SignUpEmail = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!companyName.trim()) {
      setError('Company name is required');
      return;
    }

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const existingUser = await container.authRepository.userExists(email);

      if (existingUser.status === 'success') {
        setError('An account with this email already exists');
        return;
      }

      const result = await container.authRepository.sendCode(email);

      if (result.status === 'success' && result.data?.userId) {
        useSignUpStore.getState().setUserId(result.data.userId);
        useSignUpStore.getState().setEmail(email);
        useSignUpStore.getState().setName(name);
        useSignUpStore.getState().setCompanyName(companyName);
        useSignUpStore.getState().setTermsAccepted(termsAccepted);

        navigate('/sign-up/validate');
      } else {
        setError('Something went wrong');
      }
    } catch (error) {
      setError('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight="medium" gutterBottom>
        Create an account
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        You’re a few clicks away from being in control of your inventory and purchasing. In a few steps you can create an account and start right away.
      </Typography>
      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Full Name"
          variant="outlined"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!error && error === 'Name is required'}
          helperText={error === 'Name is required' ? error : ''}
          sx={{ mb: 3 }}
        />
        <TextField
          fullWidth
          label="Company Name"
          variant="outlined"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          error={!!error && error === 'Company name is required'}
          helperText={error === 'Company name is required' ? error : ''}
          sx={{ mb: 3 }}
        />
        <TextField
          fullWidth
          type="email"
          label="Email"
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!error && error === 'Invalid email address'}
          helperText={error === 'Invalid email address' ? error : ''}
          sx={{ mb: 3 }}
        />

        <Box sx={{ mb: 3, borderRadius: 1, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.default', px: 2, py: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I agree to the{' '}
                <Link to="https://juststock.ai/algemene-voorwaarden" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </Link>
                {' '}and{' '}
                <Link to="https://juststock.ai/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </Link>
              </Typography>
            }

          />
        </Box>

        {error && <Typography variant="body2" color="error" mb={3}>{error}</Typography>}

        <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            size="medium"
            onClick={() => navigate('/sign-in')}
          >
            Sign In
          </Button>
          <Button
            variant="contained"
            type="submit"
            size="medium"
            disabled={!termsAccepted || !name.trim() || !companyName.trim() || !email.trim() || isLoading}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Creating Account...
              </Box>
            ) : (
              'Continue'
            )}
          </Button>
        </Stack>
      </form>
    </Box>
  );
};

export default SignUpEmail;